/**
 * Where an administrator is sent to grant the <b>Room operator</b> app role.
 *
 * <p>
 * <b>A link and nothing else, for ever.</b> Writing an app role assignment needs
 * <code>AppRoleAssignment.ReadWrite.All</code> — the power to grant any app role to anybody on any
 * application in the tenant — and no screen is worth that. This API reads the role and will never
 * write it (known gap 17), so the most it can do is take somebody to the one place it can be done.
 * </p>
 */

/**
 * Microsoft's own documented route to assigning users to an enterprise application is the Entra
 * admin centre, and it gives no deeper URL than this.
 *
 * <p>
 * <b>The blade deep link was considered and refused, on two grounds.</b> It is documented nowhere
 * on <code>learn.microsoft.com</code> — only on third-party writeups — and, decisively, it is keyed
 * on the service principal's OBJECT id rather than the application id. This product learns that id
 * only from the Graph read in <code>GraphDirectoryRoles</code>, which is precisely the read that has
 * failed in the <em>not checked</em> state where this link matters most. A link that disappeared
 * exactly when it was needed would be worse than one that costs three clicks.
 * </p>
 */
export const ENTRA_ADMIN_CENTRE = 'https://entra.microsoft.com';

/**
 * The tenant-scoped admin centre, or null when there is no tenant to open.
 *
 * <p>
 * <b>Scoping it to the tenant is what the link buys.</b> The remaining clicks are stated beside it
 * in {@link ROLE_GRANT_PATH}; landing in the wrong directory is the step somebody with several
 * tenants actually gets wrong, and it fails silently — the application is simply absent, which
 * reads as a registration that was never made.
 * </p>
 * <p>
 * The id is the server's and is a GUID, so the encoding below can never bite. It is there because
 * this function's job is to produce a URL of a known shape, and a value able to close the path
 * segment would make it produce one of somebody else's choosing.
 * </p>
 */
export const roleGrantUrl = (tenantId: string | null | undefined): string | null => {
  const tenant = tenantId?.trim();

  return tenant ? `${ENTRA_ADMIN_CENTRE}/${encodeURIComponent(tenant)}` : null;
};

/** The clicks the link cannot make. Stated wherever the link is offered, never only in a tooltip. */
export const ROLE_GRANT_PATH = 'Entra ID → Enterprise apps → Brush API → Users and groups';

/**
 * <b>The role is the application's, not a room's</b>, and this is the sentence that says so.
 *
 * <p>
 * It matters most in the room dialog, where the link sits inside a screen about one room and every
 * other control on it is room-scoped. Somebody granting the role from there would reasonably
 * conclude they had granted it <em>for that room</em> — and the failure is silent in both
 * directions: it also covers rooms they did not mean, and revoking it later takes all of them away
 * at once.
 * </p>
 */
export const ROLE_GRANT_SCOPE_NOTE =
  'The role is granted on the application, not on a room: granting it once covers every room this '
  + 'person is added to, and removing it takes away all of them at once.';

/** What the per-row link says it does, since "Open Entra" alone does not say why. */
export const ROLE_GRANT_LINK_TITLE =
  `Opens the Microsoft Entra admin center in a new tab: ${ROLE_GRANT_PATH}. ${ROLE_GRANT_SCOPE_NOTE}`;

/**
 * Whether this status is worth offering the link against.
 *
 * <p>
 * <b>Everything but granted, and "not checked" is deliberately included.</b> An unknown status may
 * well be an ungranted one, and the link costs nothing if it is not — where withholding it would
 * leave the one screen that knows the status could not be read offering no way to go and look.
 * An unrecognised value degrades to offering, matching how <code>roleDisplay</code> degrades it to
 * unknown: a server that grew a fourth state must not have every row quietly lose the way out.
 * </p>
 */
export const offersRoleGrantLink = (status: string): boolean => status !== 'granted';
